import { memo, useMemo, type FC, type SyntheticEvent } from 'react';
import { useMediaQuery } from 'react-responsive';

import dayjs from '@/js/libs/dayjs/index.ts';
import { useCallbackRef } from '@/js/libs/hooks/useCallbackRef/index.ts';

import { Media } from '../../../../../theme/Media.tsx';
import { Select } from '../../../Select/index.tsx';
import type { DatePickerTranslations, DateType, Option, TimePreset } from '../../types.ts';
import { PresetItem } from './PresetItem.tsx';
import { Header, MobileHeader, PresetsWrapper } from './styled.ts';

interface TimePresetsProps {
  presets: TimePreset[];
  date: DateType;
  onChange: (dateRange: Date | null, e?: SyntheticEvent<HTMLDivElement, MouseEvent>) => void;
  isDateSelected: boolean;
  dataTestId?: string;
  translations: Required<DatePickerTranslations>;
}

export const TimePresets: FC<TimePresetsProps> = memo(function TimePresets({
  presets,
  date,
  onChange,
  isDateSelected,
  dataTestId,
  translations,
}) {
  const title = translations.time;
  const isMobile = useMediaQuery({ query: Media.xsMax });

  const selectOptions = useMemo<Option<DateType>[]>(
    () =>
      presets.map(({ title, value }) => ({
        label: title,
        value,
      })),
    [presets],
  );

  const selectedValue = useMemo<Option<DateType> | undefined>(() => {
    const activePreset = presets.find(({ isActive, value }) => isActive(value, date));
    return activePreset ? { label: activePreset.title, value: activePreset.value } : undefined;
  }, [date, presets]);

  const applyTimePreset = useCallbackRef((presetValue: DateType) => {
    if (!date) return;

    const timePresetDate = dayjs(presetValue);
    const currentDate = dayjs(date);
    if (timePresetDate.isValid() && currentDate.isValid()) {
      const updatedValue = currentDate.hour(timePresetDate.hour()).minute(timePresetDate.minute());
      onChange(updatedValue.toDate());
    }
  });

  const handleSelectChange = useCallbackRef((option: Option<DateType> | null) => {
    if (!option) return;
    applyTimePreset(option.value);
  });

  const createTimePresetClickHandler = useCallbackRef((value: DateType) => () => {
    if (!isDateSelected) return;
    applyTimePreset(value);
  });

  if (!isDateSelected) {
    return null;
  }

  return (
    <div>
      {isMobile ? (
        <div>
          <MobileHeader>{title}</MobileHeader>
          <Select options={selectOptions} value={selectedValue} onChange={handleSelectChange} isDisabled={!isDateSelected} />
        </div>
      ) : (
        <>
          <Header>{title}</Header>
          <PresetsWrapper data-testid={dataTestId ? `${dataTestId}-time-presets` : undefined}>
            {presets.map(({ isActive, title, value }) => {
              const isActivePreset = isActive(value, date);
              const presetTestId = dataTestId ? `${dataTestId}-time-preset-${title.replace(':', '')}` : undefined;
              return (
                <PresetItem
                  key={title}
                  title={title}
                  isActive={isActivePreset}
                  onClick={createTimePresetClickHandler(value)}
                  dataTestId={presetTestId}
                />
              );
            })}
          </PresetsWrapper>
        </>
      )}
    </div>
  );
});
